import { type IChatCounterpart } from '../../../interface/peer-chat';
import { avatarTint, initials } from '../../pages/child-agent/utils';
import { CopyValue } from '../../utils/copy';
import { StatusBadge, type StatusTone } from '../../utils/status-badge';

const KebabIcon = () => (
	<svg
		viewBox='0 0 20 20'
		fill='currentColor'
		aria-hidden='true'
		width='20'
		height='20'
	>
		<circle
			cx='10'
			cy='4'
			r='1.8'
		/>
		<circle
			cx='10'
			cy='10'
			r='1.8'
		/>
		<circle
			cx='10'
			cy='16'
			r='1.8'
		/>
	</svg>
);

interface IChatHeaderStrip {
	counterpart: IChatCounterpart;
	/** "Polling-unit agent", "Ward agent"… */
	roleLabel: string;
	/** Presence as a sentence. Never a dot. */
	presence: string;
	/** The pinned unit-state chip. Null while nothing is known. */
	chip: {
		label: string;
		tone: StatusTone;
		title?: string;
		/** The unit the chip describes; defaults to the counterpart's. */ unitCode?: string;
	} | null;
	onChip?: () => void;
	onMore: () => void;
}

const splitName = (name: string): [string, string] => {
	const parts = name.trim().split(/\s+/);
	return [parts[0] ?? '', parts.slice(1).join(' ')];
};

/**
 * The identity strip under a peer conversation's title: avatar, level + unit code, presence,
 * and the live result-state chip.
 *
 * <p>This strip IS the thing that tells a peer conversation from the support one. Colour
 * cannot be: in dark mode the primary and success tokens collapse into each other, and the
 * bubbles must stay identical across both conversations so muscle memory holds. The strip is
 * the same component language a parent already reads on Agent Coverage, so recognition is
 * free — and it answers the question the reader actually has: which unit is this about?</p>
 */
const ChatHeaderStrip = ({ counterpart, roleLabel, presence, chip, onChip, onMore }: IChatHeaderStrip) => {
	const chipCode = chip?.unitCode ?? counterpart.entityCode;
	const [first, last] = splitName(counterpart.name);
	const seed = counterpart.entityCode || counterpart.name;

	return (
		<div className='sc-header-strip'>
			<div className='sc-header-strip__row'>
				<span
					className='sc-header-strip__avatar'
					style={{ background: avatarTint(seed) }}
					aria-hidden='true'
				>
					{initials(first, last, counterpart.name)}
				</span>
				<div className='sc-header-strip__text'>
					<span className='sc-header-strip__eyebrow'>
						{roleLabel}
						{counterpart.entityCode ? (
							<>
								{' · '}
								<span className='sc-header-strip__code'>{counterpart.entityCode}</span>
								<CopyValue
									value={counterpart.entityCode}
									label='unit code'
									size={14}
								/>
							</>
						) : null}
					</span>
					<span className='sc-header-strip__presence'>{presence}</span>
				</div>
				<button
					type='button'
					className='sc-header-strip__more'
					onClick={onMore}
					aria-label='More actions'
					aria-haspopup='dialog'
				>
					<KebabIcon />
				</button>
			</div>
			{chip ? (
				<div className='sc-header-strip__chip'>
					{onChip ? (
						<button
							type='button'
							className='sc-header-strip__chip-button'
							onClick={onChip}
							aria-label={`${chipCode ?? 'Unit'} ${chip.label}. Open the result.`}
							title={chip.title}
						>
							<StatusBadge
								label={chipCode ? `${chipCode} · ${chip.label}` : chip.label}
								tone={chip.tone}
								size='md'
							/>
						</button>
					) : (
						<StatusBadge
							label={chipCode ? `${chipCode} · ${chip.label}` : chip.label}
							tone={chip.tone}
							size='md'
							title={chip.title}
						/>
					)}
				</div>
			) : null}
		</div>
	);
};

export default ChatHeaderStrip;
